/**
 * ScatterChart - a two-measure chart with scatter and bubble variants.
 *
 * Built on recharts. Accepts arbitrary row shapes through configurable keys,
 * drops what cannot be positioned, and always resolves to a visible state
 * (loading, error, empty, or chart) so it can be dropped straight onto a
 * dashboard without guard code around it.
 *
 * Two things set this apart from the other charts here:
 *
 * - Bubble area, not radius, carries the size measure. Readers compare ink, so
 *   the radius grows with the square root of the value; feeding the value to
 *   the radius directly squares every difference.
 * - Groups cap at three. Scattered marks have no fixed order, so any two can
 *   end up side by side, and only three hues clear that harder colour test.
 *   The tail folds into a neutral bucket rather than growing a fourth hue.
 *
 * Usage:
 *   <ScatterChart data={rows} xKey="likelihood" yKey="impact" />
 *   <ScatterChart variant="bubble" data={rows} zKey="assets" groupKey="tier" />
 */

import { useCallback, useMemo, useState } from "react";
import {
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Scatter,
  ScatterChart as RechartsScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { cn } from "../../ui/utils";
import {
  CHART_PALETTE_ALLPAIRS_DARK,
  CHART_PALETTE_ALLPAIRS_LIGHT,
  OTHER_COLOR_DARK,
  OTHER_COLOR_LIGHT,
  SURFACE_DARK,
  SURFACE_LIGHT,
  AXIS_TICK_FONT_SIZE,
  AXIS_TICK_FONT_WEIGHT,
  formatCompact,
  formatNumber,
  resolveErrorMessage,
  resolvePlotHeight,
  useElementWidth,
  useIsDarkTheme,
  usePrefersReducedMotion,
} from "../chartUtils";
import {
  ChartEmptyState,
  ChartErrorBoundary,
  ChartErrorState,
  ChartLoadingState,
  ChartWarnings,
} from "../ChartStates";
import { BubbleSizeKey, PointTable, PointTooltip, SeriesLegend } from "./ScatterChrome";
import {
  diamondPoints,
  markExtent,
  normalizeScatterData,
  type GroupInput,
  type MarkShape,
  type ScatterPoint,
} from "./scatterUtils";

/**
 * Hairlines one step off the surface, so the grid never competes with data.
 *
 * Light was `#e2e8f0` — the border token — which reads at roughly 1.1:1 on a white card and is
 * effectively invisible. A gridline has to be faint, but it still has to be a line.
 */
const GRID_LIGHT = "#cbd5e1";
const GRID_DARK = "rgba(255,255,255,0.16)";
const AXIS_TEXT_LIGHT = "#64748b";
const AXIS_TEXT_DARK = "rgba(255,255,255,0.55)";

/** Below this, a pointer cannot reliably land on a small mark. */
const MIN_HIT_RADIUS = 12;


export interface ScatterChartProps {
  /**
   * DOM id for the chart's root element. Stays on the same element across the
   * loading, error, empty, and plotted states, so anything anchored to it does
   * not lose its target when the state changes. Also seeds the ids of the
   * internal title and plot nodes.
   */
  id?: string;
  /**
   * Identifier for the chart instance, used for tests, analytics, and event
   * handlers. Rendered as `data-name`, since `name` is not a valid attribute on
   * a div, and passed back as the last argument to onPointClick.
   */
  name?: string;

  /** Rows to plot. Null, undefined, and non-arrays resolve to the empty state. */
  data: unknown;
  /** Bubble sizes each mark by `zKey`; scatter draws every mark alike. */
  variant?: "scatter" | "bubble";

  /** Field holding each row's horizontal measure. */
  xKey?: string;
  /** Field holding each row's vertical measure. */
  yKey?: string;
  /** Field driving mark area. Required by the bubble variant. */
  zKey?: string;
  /** Field splitting points into coloured groups. */
  groupKey?: string;
  /** Field naming each point in the tooltip and table. */
  labelKey?: string;
  /** Fixes group order, labels, and colours ahead of the data. */
  groups?: GroupInput[];

  groupLabel?: string;

  title?: string;
  subtitle?: string;
  /** Axis captions. Worth setting: position is meaningless without them. */
  xAxisLabel?: string;
  yAxisLabel?: string;

  /** Plot height in px, excluding the header, legend, and table. */
  height?: number;
  /** Replaces the built-in hues. Only the first `maxGroups` are ever used. */
  palette?: readonly string[];

  loading?: boolean;
  /** Fetch failure. Takes precedence over every other state. */
  error?: string | Error | null;
  /** Retry handler shown on the error state. */
  onRetry?: () => void;
  emptyMessage?: string;

  /**
   * Colour cap. Defaults to three, the most that stay distinct when any two
   * marks can be neighbours. Raising it trades away that guarantee.
   */
  maxGroups?: number;
  otherLabel?: string;

  showLegend?: boolean;
  legendPosition?: "top" | "bottom";
  /** Allow clicking legend items to hide and restore groups. */
  legendToggle?: boolean;

  showGrid?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  xStartsAtZero?: boolean;
  yStartsAtZero?: boolean;

  /**
   * Mark silhouette. Diamonds are area-matched to the circle they replace, so
   * switching changes how a mark looks and not what its size is worth.
   *
   * Orthogonal to `variant`: that decides whether size encodes `zKey` at all,
   * this decides the shape drawn at whatever size results.
   */
  markShape?: MarkShape;
  /** Mark radius in px for the scatter variant. */
  dotRadius?: number;
  /** Smallest and largest bubble radius in px. */
  bubbleRadiusRange?: [number, number];
  /**
   * Mark opacity. Below 1 lets overlapping marks show through each other, which
   * is what makes a dense cloud readable.
   */
  markOpacity?: number;
  /** Key showing what bubble area is worth. On by default for bubbles. */
  showSizeKey?: boolean;

  showTooltip?: boolean;
  /** Expandable text equivalent of the chart. */
  showDataTable?: boolean;
  /** Surface notes about dropped points and folded groups. */
  showWarnings?: boolean;

  /** Suffix appended to y values, for example "%" or " ms". */
  unit?: string;
  valueFormatter?: (value: number) => string;
  xFormatter?: (value: number) => string;
  sizeFormatter?: (value: number) => string;

  onPointClick?: (point: ScatterPoint, index: number, name?: string) => void;

  className?: string;
  /** Overrides the generated chart description read by screen readers. */
  ariaLabel?: string;
}

function ScatterChartInner({
  id,
  name,
  data,
  variant = "scatter",
  xKey = "x",
  yKey = "y",
  zKey = "z",
  groupKey,
  labelKey,
  groups: groupInput,
  groupLabel = "Group",
  title,
  subtitle,
  xAxisLabel,
  yAxisLabel,
  height = 280,
  palette,
  loading = false,
  error = null,
  onRetry,
  emptyMessage = "There are no records to chart for the current selection.",
  maxGroups = 3,
  otherLabel = "Other",
  showLegend = true,
  legendPosition = "top",
  legendToggle = true,
  showGrid = true,
  showXAxis = true,
  showYAxis = true,
  xStartsAtZero = false,
  yStartsAtZero = false,
  markShape = "circle",
  dotRadius = 5,
  bubbleRadiusRange = [5, 22],
  markOpacity = 0.78,
  showSizeKey,
  showTooltip = true,
  showDataTable = false,
  showWarnings = true,
  unit = "",
  valueFormatter,
  xFormatter,
  sizeFormatter,
  onPointClick,
  className,
  ariaLabel,
}: ScatterChartProps) {
  const isDark = useIsDarkTheme();
  const reduceMotion = usePrefersReducedMotion();
  const [containerRef, containerWidth] = useElementWidth<HTMLDivElement>();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());
  const [tableOpen, setTableOpen] = useState(false);

  const plotHeight = resolvePlotHeight(height, 280);
  const isBubble = variant === "bubble";

  const activePalette = useMemo(() => {
    const source = palette?.filter?.(Boolean);
    if (source?.length) return source;
    // The all-pairs subset, not the eight-slot order the other charts use.
    return isDark ? CHART_PALETTE_ALLPAIRS_DARK : CHART_PALETTE_ALLPAIRS_LIGHT;
  }, [palette, isDark]);

  const safeFormat = useCallback(
    (fn: ((value: number) => string) | undefined, value: number) => {
      if (typeof fn === "function") {
        try {
          return fn(value);
        } catch {
          // A throwing formatter must not take the chart down with it.
          return formatNumber(value);
        }
      }
      return formatNumber(value);
    },
    [],
  );

  const formatY = useCallback(
    (value: number) => safeFormat(valueFormatter, value),
    [safeFormat, valueFormatter],
  );
  const formatX = useCallback(
    (value: number) => safeFormat(xFormatter, value),
    [safeFormat, xFormatter],
  );
  const formatZ = useCallback(
    (value: number) => safeFormat(sizeFormatter, value),
    [safeFormat, sizeFormatter],
  );

  const { groups, points, xDomain, yDomain, xTicks, yTicks, zRange, warnings } = useMemo(
    () =>
      normalizeScatterData(data, {
        xKey,
        yKey,
        zKey: isBubble ? zKey : undefined,
        groupKey,
        labelKey,
        groups: groupInput,
        palette: activePalette,
        otherColor: isDark ? OTHER_COLOR_DARK : OTHER_COLOR_LIGHT,
        otherLabel,
        maxGroups,
        radiusRange: bubbleRadiusRange,
        baseRadius: dotRadius,
        xStartsAtZero,
        yStartsAtZero,
      }),
    [
      data,
      xKey,
      yKey,
      zKey,
      isBubble,
      groupKey,
      labelKey,
      groupInput,
      activePalette,
      isDark,
      otherLabel,
      maxGroups,
      bubbleRadiusRange,
      dotRadius,
      xStartsAtZero,
      yStartsAtZero,
    ],
  );

  // A single unnamed group is not an identity worth a legend; the title says
  // what is plotted.
  const hasGroups = groups.length > 1 || (groups.length === 1 && !!groups[0]?.label);

  const visibleGroups = useMemo(
    () => groups.filter((group) => !hiddenIds.has(group.id)),
    [groups, hiddenIds],
  );
  const visiblePoints = useMemo(
    () => visibleGroups.flatMap((group) => group.points),
    [visibleGroups],
  );

  const handleToggle = useCallback(
    (groupId: string) => {
      if (!legendToggle) return;
      setHiddenIds((prev) => {
        const next = new Set(prev);
        if (next.has(groupId)) {
          next.delete(groupId);
          return next;
        }
        // Refuse the toggle that would empty the chart.
        if (prev.size + 1 >= groups.length) return prev;
        next.add(groupId);
        return next;
      });
    },
    [legendToggle, groups.length],
  );

  const handlePointClick = useCallback(
    (point: ScatterPoint) => {
      if (typeof onPointClick !== "function" || !point) return;
      // recharts hands back a merged copy of the datum, so the index has to
      // ride on the point itself rather than be searched for by reference.
      onPointClick(point, point.index ?? -1, name);
    },
    [onPointClick, name],
  );

  const xLabel = xAxisLabel || xKey;
  const yLabel = yAxisLabel || yKey;
  // Gated on the measure existing, not on the variant. A caller can name a third measure without
  // asking area to carry it — worth reading in the tooltip and the table either way, and a narrow
  // value range is exactly the case where sizing misleads and a plain number does not. The size
  // key stays variant-gated below, since with uniform marks there is no area to explain.
  const zLabel = zRange ? sizeLabel(zKey) : undefined;

  const description = useMemo(() => {
    if (ariaLabel) return ariaLabel;
    const kind = isBubble ? "Bubble" : "Scatter";
    const base = title ? `${kind} chart: ${title}.` : `${kind} chart.`;
    if (!visiblePoints.length) return `${base} No data.`;
    const axes = `${xLabel} on the horizontal axis, ${yLabel} on the vertical axis.`;
    const size = zLabel ? ` Mark area shows ${zLabel}.` : "";
    const grouping = hasGroups
      ? ` ${visibleGroups.length} groups: ${visibleGroups.map((g) => g.label).join(", ")}.`
      : "";
    return `${base} ${visiblePoints.length} points. ${axes}${size}${grouping}`;
  }, [
    ariaLabel,
    isBubble,
    title,
    visiblePoints.length,
    visibleGroups,
    hasGroups,
    xLabel,
    yLabel,
    zLabel,
  ]);

  const errorMessage = resolveErrorMessage(error);
  const gridColor = isDark ? GRID_DARK : GRID_LIGHT;
  const axisTextColor = isDark ? AXIS_TEXT_DARK : AXIS_TEXT_LIGHT;
  const surface = isDark ? SURFACE_DARK : SURFACE_LIGHT;
  const compactAxis = containerWidth > 0 && containerWidth < 380;
  const sizeKeyOn = (showSizeKey ?? isBubble) && !!zRange && !!zLabel;

  /**
   * Draws one mark: a transparent hit circle under a ringed dot or diamond.
   *
   * The hit circle is the point of it. A 5px dot is a pinpoint nobody lands on,
   * so the pointer target is padded to at least 24px across while the painted
   * mark stays small enough to show its neighbours. It stays a circle whatever
   * the mark is — it is invisible, and a round target forgives a near miss at a
   * diamond's flat edge, where the shape itself has nothing to hit.
   */
  // recharts types the shape slot loosely, so the payload arrives untyped.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderMark = useCallback(
    (props: any) => {
      const { cx, cy, payload } = props ?? {};
      if (!Number.isFinite(cx) || !Number.isFinite(cy) || !payload) return <g />;
      const point = payload as ScatterPoint;
      const r = Number.isFinite(point.r) ? point.r : dotRadius;
      // How far the painted mark actually reaches from its centre. A diamond
      // covers more ground than its circle-equivalent radius, so the hit target
      // is sized from this rather than from r.
      const extent = markExtent(r, markShape);
      const dimmed = activeId != null && activeId !== point.groupId;

      return (
        <g style={{ opacity: dimmed ? 0.2 : 1, transition: "opacity 200ms ease" }}>
          <circle cx={cx} cy={cy} r={Math.max(MIN_HIT_RADIUS, extent + 4)} fill="transparent" />
          {markShape === "diamond" ? (
            <polygon
              points={diamondPoints(cx, cy, extent)}
              fill={point.color}
              fillOpacity={markOpacity}
              // The surface ring keeps overlapping marks separable — and matters
              // more here than for circles, since two diamonds meeting at their
              // tips read as one shape without it.
              stroke={surface}
              strokeWidth={2}
            />
          ) : (
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill={point.color}
              fillOpacity={markOpacity}
              // The surface ring keeps overlapping marks separable.
              stroke={surface}
              strokeWidth={2}
            />
          )}
        </g>
      );
    },
    [activeId, dotRadius, markOpacity, markShape, surface],
  );

  const header = (title || subtitle) && (
    <div className="mb-3 min-w-0">
      {title && (
        <h3
          id={id ? `${id}-title` : undefined}
          className="break-words text-sm font-bold leading-snug text-foreground"
        >
          {title}
        </h3>
      )}
      {subtitle && (
        <p className="mt-0.5 break-words text-xs leading-snug text-muted-foreground">{subtitle}</p>
      )}
    </div>
  );

  const legend = showLegend && hasGroups && (
    <SeriesLegend
      series={groups.map((group) => ({
        id: group.id,
        key: group.id,
        label: group.label,
        color: group.color,
      }))}
      activeId={activeId}
      hiddenIds={hiddenIds}
      interactive={legendToggle}
      // Follows the mark rather than being fixed. Hardcoding a shape here is what left the legend
      // showing dots after the marks became diamonds — the key described a chart that had moved on.
      swatch={markShape === "diamond" ? "diamond" : "dot"}
      onActivate={setActiveId}
      onToggle={handleToggle}
    />
  );

  /* Error and loading short-circuit the plot but keep the header in place, so
     the surrounding layout never jumps or blanks between states. */
  if (errorMessage) {
    return (
      <div id={id} data-name={name} ref={containerRef} className={cn("w-full min-w-0", className)}>
        {header}
        <ChartErrorState height={plotHeight} message={errorMessage} onRetry={onRetry} />
      </div>
    );
  }

  if (loading) {
    return (
      <div id={id} data-name={name} ref={containerRef} className={cn("w-full min-w-0", className)}>
        {header}
        <ChartLoadingState height={plotHeight} shape="cartesian" />
      </div>
    );
  }

  if (!visiblePoints.length) {
    return (
      <div id={id} data-name={name} ref={containerRef} className={cn("w-full min-w-0", className)}>
        {header}
        <ChartEmptyState height={plotHeight} message={emptyMessage} shape="cartesian" />
        {showWarnings && <ChartWarnings warnings={warnings} />}
      </div>
    );
  }

  return (
    <div id={id} data-name={name} ref={containerRef} className={cn("w-full min-w-0", className)}>
      {header}

      {legendPosition === "top" && legend && <div className="mb-2">{legend}</div>}

      <div
        id={id ? `${id}-plot` : undefined}
        className="w-full"
        style={{ height: plotHeight }}
        role="img"
        aria-label={description}
      >
        <ResponsiveContainer width="100%" height="100%">
          <RechartsScatterChart
            margin={{
              top: 8,
              // Room for the largest mark to sit near an edge without clipping.
              right: isBubble ? bubbleRadiusRange[1] + 4 : 12,
              left: yAxisLabel ? 8 : 0,
              bottom: xAxisLabel ? 16 : 0,
            }}
          >
            {showGrid && (
              // Hairlines both ways: on a two-measure plot the reader has to
              // track position on each axis, not just one. Dashed to match the
              // platform's other cartesian charts, and so the grid reads as a
              // ruler behind the marks rather than as content beside them.
              <CartesianGrid stroke={gridColor} strokeWidth={1} strokeDasharray="3 3" />
            )}

            <XAxis
              type="number"
              dataKey="x"
              domain={xDomain}
              hide={!showXAxis}
              tick={{
                fill: axisTextColor,
                fontSize: AXIS_TICK_FONT_SIZE,
                fontWeight: AXIS_TICK_FONT_WEIGHT,
              }}
              tickFormatter={(value: number) => formatCompact(value)}
              axisLine={{ stroke: gridColor }}
              tickLine={false}
              height={xAxisLabel ? 42 : 28}
              // Explicit round ticks: recharts would otherwise divide the
              // domain evenly and label the axis with arithmetic like 4.65.
              ticks={compactAxis ? xTicks.filter((_, i) => i % 2 === 0) : xTicks}
              label={
                xAxisLabel
                  ? {
                      value: xAxisLabel,
                      position: "insideBottom",
                      offset: -4,
                      style: { fill: axisTextColor, fontSize: 11, fontWeight: 600 },
                    }
                  : undefined
              }
            />

            <YAxis
              type="number"
              dataKey="y"
              domain={yDomain}
              hide={!showYAxis}
              tick={{
                fill: axisTextColor,
                fontSize: AXIS_TICK_FONT_SIZE,
                fontWeight: AXIS_TICK_FONT_WEIGHT,
              }}
              tickFormatter={(value: number) => formatCompact(value)}
              axisLine={false}
              tickLine={false}
              width={compactAxis ? 36 : 46}
              ticks={compactAxis ? yTicks.filter((_, i) => i % 2 === 0) : yTicks}
              label={
                yAxisLabel
                  ? {
                      value: yAxisLabel,
                      angle: -90,
                      position: "insideLeft",
                      style: {
                        fill: axisTextColor,
                        fontSize: 11,
                        fontWeight: 600,
                        textAnchor: "middle",
                      },
                    }
                  : undefined
              }
            />

            {/* Radius is precomputed on each point, so the z scale is held flat
                here and the size encoding stays area-proportional. */}
            <ZAxis type="number" dataKey="r" range={[dotRadius, dotRadius]} />

            {showTooltip && (
              <Tooltip
                content={
                  <PointTooltip
                    xLabel={xLabel}
                    yLabel={yLabel}
                    zLabel={zLabel}
                    unit={unit}
                    xFormatter={formatX}
                    yFormatter={formatY}
                    zFormatter={formatZ}
                    showGroup={hasGroups}
                    groupLabel={groupLabel}
                    markShape={markShape}
                  />
                }
                // A crosshair would be wrong here: marks do not share an x.
                cursor={false}
                wrapperStyle={{ outline: "none", zIndex: 20 }}
                isAnimationActive={!reduceMotion}
              />
            )}

            {visibleGroups.map((group) => (
              <Scatter
                key={group.id}
                name={group.label || title || "Points"}
                data={group.points}
                fill={group.color}
                shape={renderMark}
                isAnimationActive={!reduceMotion}
                animationDuration={600}
                onClick={onPointClick ? handlePointClick : undefined}
                className={onPointClick ? "cursor-pointer" : undefined}
              >
                {group.points.map((point, i) => (
                  <Cell key={i} fill={point.color} />
                ))}
              </Scatter>
            ))}
          </RechartsScatterChart>
        </ResponsiveContainer>
      </div>

      {(legendPosition === "bottom" && legend) || sizeKeyOn ? (
        <div className="mt-2 flex flex-col items-center gap-1.5">
          {legendPosition === "bottom" && legend}
          {sizeKeyOn && zRange && zLabel && (
            <BubbleSizeKey
              zLabel={zLabel}
              zRange={zRange}
              radiusRange={bubbleRadiusRange}
              formatter={formatZ}
              markShape={markShape}
            />
          )}
        </div>
      ) : null}

      {showWarnings && <ChartWarnings warnings={warnings} />}

      {showDataTable && (
        <PointTable
          points={visiblePoints}
          xLabel={xLabel}
          yLabel={yLabel}
          zLabel={zLabel}
          unit={unit}
          showGroup={hasGroups}
          open={tableOpen}
          xFormatter={formatX}
          yFormatter={formatY}
          zFormatter={formatZ}
          onToggle={() => setTableOpen((open) => !open)}
          markShape={markShape}
        />
      )}
    </div>
  );
}

/** Turns a size key into a caption for the tooltip, table, and size legend. */
function sizeLabel(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Public entry point. Wraps the chart in a local error boundary so a render
 * crash stays inside the chart box instead of reaching the app-level boundary
 * and blanking the page.
 */
export function ScatterChart(props: ScatterChartProps) {
  const plotHeight = resolvePlotHeight(props?.height, 280);

  return (
    <ChartErrorBoundary height={plotHeight} onRetry={props?.onRetry}>
      <ScatterChartInner {...props} />
    </ChartErrorBoundary>
  );
}

export default ScatterChart;

export type { GroupInput, ScatterPoint };
